import { useContextSelector } from "use-context-selector";
import querySelector from "utils/query-selector";

import { IStore, StoreContext } from "../../store-context";
import {
  CurrencyType,
  DealBarCharacteristic,
  DealFolderForm,
  DealsQueries,
  FilesQueryBy,
  FolderQueryBy,
  FormQueryBy,
  IAssetClass,
  ICapitalSeeker,
  ICommitment,
  IDeal,
  IDealModule,
  IDealRoundsWithInvestors,
  IEmail,
  IFile,
  IFileMeta,
  IFolder,
  IInvestmentDetailChartConfig,
  IInvestor,
  INewsMeta,
  INote,
  INoteLock,
  INoteMeta,
  InvestorWithBank,
  IOpportunities,
  IReachOutManage,
  IRound,
  IStatus,
  IUploadingProgress,
  IWorkspaceMetadata,
  Measure,
  MeasureParameter,
  NoteQueryBy
} from "../../types";

export const useDealSelector = (): IDeal => useContextSelector(StoreContext, (store: IStore) => store.dealModule.deal);

// export const useDealSectionsSelector = (): ISection[] | undefined =>
//   useContextSelector(
//     StoreContext,
//     (store: IStore) => store.dealModule.deal.sections
//   );

export const useDealTitleSelector = (): string | undefined =>
  useContextSelector(StoreContext, (store: IStore) => store.dealModule.deal?.title);

export const useDealDefaultCurrencySelect = (): CurrencyType | null =>
  useContextSelector(StoreContext, (store: IStore) => {
    const currencies = store.dealModule.deal.currency?.data;

    if (!currencies) return null;

    const defaultCurrency = currencies.find((cur) => cur.is_default);

    if (!defaultCurrency) return null;

    return defaultCurrency.value as CurrencyType;
  });

export const useDealVintageSelect = () =>
  useContextSelector(StoreContext, (store: IStore) => store.dealModule.deal.vintage);

export const useDealUploadsSelector = (): IUploadingProgress[] =>
  useContextSelector(StoreContext, (store: IStore) => store.dealModule.uploadingProgress);

export const useDealIdSelector = (): number | undefined =>
  useContextSelector(StoreContext, (store: IStore) => store.dealModule.deal?.id);

export const useDealAccountLogoSelector = (): string =>
  useContextSelector(StoreContext, (store: IStore) => store.dealModule.deal?.account_logo);

export const useDealStatusesSelector = (): IStatus[] =>
  useContextSelector(StoreContext, (store: IStore) => store.dealModule.statuses);

export const useDealStatusSelector = (): IStatus | undefined =>
  useContextSelector(StoreContext, (store: IStore) => store.dealModule.deal.status);

export const useDealStageIdSelector = (): IDeal["deal_stage_id"] | undefined =>
  useContextSelector(StoreContext, (store: IStore) => store.dealModule.deal.deal_stage_id);

export const useDealEmailSelector = (): string | undefined =>
  useContextSelector(StoreContext, (store: IStore) => store.dealModule.deal.email);

export const useDealOwnerIdSelector = (): number =>
  useContextSelector(StoreContext, (store: IStore) => store.dealModule.deal.user_id);

export const useFileMetaSelector = (): Partial<IFileMeta> =>
  useContextSelector(StoreContext, (state: IStore) => state.dealModule.fileMeta);

export const useFolderMetaSelector = (): Partial<IWorkspaceMetadata> =>
  useContextSelector(StoreContext, (state: IStore) => state.dealModule.folderMeta);

export const useNewsMetaSelector = (): Partial<INewsMeta> =>
  useContextSelector(StoreContext, (state: IStore) => state.dealModule.newsMeta);

export const useEmailMetaSelector = () =>
  useContextSelector(StoreContext, (state: IStore) => state.dealModule.emailMeta);

// export const useEmailsSectionSelector = (): IEmail[] =>
//   useContextSelector(
//     StoreContext,
//     (state: IStore) =>
//       state.dealModule.deal?.sections?.find(
//         (folder: ISection) => folder.kind === "email"
//       )?.emails || []
//   );

export const useInvestorsSelector = (): InvestorWithBank[] =>
  useContextSelector(StoreContext, (state: IStore) => state.dealModule.investors);

export const useInvestorByIdSelector = (id: number): InvestorWithBank | undefined =>
  useContextSelector(StoreContext, (state: IStore) =>
    state.dealModule.investors.find((investor) => investor.id === id)
  );

export const useNoteMetaSelector = (): Partial<INoteMeta> =>
  useContextSelector(StoreContext, (state: IStore) => state.dealModule.noteMeta);

export const useNoteLockSelector = (): INoteLock =>
  useContextSelector(StoreContext, (state: IStore) => state.dealModule.noteLock);

export const useRoundsSelector = (): IRound[] =>
  useContextSelector(StoreContext, (state: IStore) => state.dealModule.deal?.rounds);

export const useCommitmentsSelector = (): ICommitment[] =>
  useContextSelector(StoreContext, (state: IStore) =>
    state.dealModule.deal.rounds.reduce((acc, item: IRound) => [...acc, ...item.deal_commitments], [] as ICommitment[])
  );

export const useDealInvestorsSelector = (): IInvestor[] =>
  useContextSelector(StoreContext, (state: IStore) => {
    const investors: IInvestor[] = [];
    state.dealModule.deal.rounds.forEach((round) => {
      round.deal_commitments.forEach((commitment) => investors.push(commitment.investor));
    });
    return investors;
  });

export const useDealGeographySelector = () =>
  useContextSelector(StoreContext, (state: IStore) => state.dealModule.deal.geography);

export const useMeasureParametersSelector = (): MeasureParameter[] =>
  useContextSelector(StoreContext, (state: IStore) => state.dealModule.measureParameters);

export const useDealQueriesSelect = (): DealsQueries =>
  useContextSelector(StoreContext, (state: IStore) => state.dealModule.queries);

export const useDealCSSelector = (): ICapitalSeeker[] =>
  useContextSelector(StoreContext, (state: IStore) => state.dealModule.deal.deal_capital_seeker);

export const useDealMetaFoldersSelector = (): Record<number, IFolder> =>
  useContextSelector(StoreContext, (state: IStore) => state.dealModule.folders);

// ---------------------------------- branched structure selectors ----------------------------
// folders
export const useAllFoldersSelect = (): IFolder[] =>
  useContextSelector(StoreContext, (state) => Object.values(state.dealModule.folders));

export const useFoldersSelect = (where: FolderQueryBy): IFolder[] =>
  useContextSelector(StoreContext, (state) => querySelector(state.dealModule.folders, where));

export const useFolderOneSelect = (where: FolderQueryBy): IFolder | undefined =>
  useContextSelector(StoreContext, (state) => querySelector(state.dealModule.folders, where)[0]);

export const useFolderSelect = (id?: number): IFolder | undefined =>
  useContextSelector(StoreContext, (state) => {
    if (id != null) {
      return state.dealModule.folders[id];
    }
  });

export const useFoldersNumberSelect = (): number =>
  useContextSelector(StoreContext, (state) => Object.values(state.dealModule.folders).length);

export const useFindOneFolder = (where: FolderQueryBy): IFolder | undefined =>
  useContextSelector(StoreContext, (state) => querySelector(state.dealModule.folders, where)[0]);

export const useFolderChildrenIdsSelect = (id: number): number[] => {
  return useContextSelector(StoreContext, (state) => {
    const result: number[] = [];

    const { folders } = state.dealModule;

    if (!Object.keys(folders).length) return result;

    const addChildFolder = (id: number) => {
      for (const key in folders) {
        const currentFolder = folders[key];

        // if no child folder
        if (currentFolder.parent_id !== id) continue;

        result.push(currentFolder.id);

        addChildFolder(currentFolder.id);
      }
    };

    addChildFolder(id);

    return result;
  });
};

// Forms
export const useAllFormsSelect = (): DealFolderForm[] =>
  useContextSelector(StoreContext, (state) => Object.values(state.dealModule.forms));

export const useAllFormsObjectSelect = (): Record<number, DealFolderForm> =>
  useContextSelector(StoreContext, (state) => state.dealModule.forms);

export const useFormsNumberSelect = (): number =>
  useContextSelector(StoreContext, (state) => Object.values(state.dealModule.forms).length);

export const useFirstFormSelect = (): DealFolderForm | undefined =>
  useContextSelector(StoreContext, (state) => Object.values(state.dealModule.forms)[0]);

export const useFormsSelect = (where: FormQueryBy): DealFolderForm[] =>
  useContextSelector(StoreContext, (state) => querySelector(state.dealModule.forms, where));

export const useFormSelect = (id: number): DealFolderForm | undefined =>
  useContextSelector(StoreContext, (state) => state.dealModule.forms[id]);

export const useFindOneForm = (where: FormQueryBy): DealFolderForm | undefined =>
  useContextSelector(StoreContext, (state) => querySelector(state.dealModule.forms, where)[0]);

// files
export const useFilesSelect = (where: FilesQueryBy): IFile[] =>
  useContextSelector(StoreContext, (state) => querySelector(state.dealModule.files, where));

export const useFileSelect = (id: number): IFile | undefined =>
  useContextSelector(StoreContext, (state) => state.dealModule.files[id]);

export const useAllFilesSelect = (): IFile[] =>
  useContextSelector(StoreContext, (state) => Object.values(state.dealModule.files));

export const useAllFilesObjectSelect = (): Record<number, IFile> =>
  useContextSelector(StoreContext, (state) => state.dealModule.files);

// emails
export const useAllEmailsSelect = (): IEmail[] =>
  useContextSelector(StoreContext, (state) => Object.values(state.dealModule.emails));

export const useEmailsSelect = (where: FilesQueryBy): IEmail[] =>
  useContextSelector(StoreContext, (state) => querySelector(state.dealModule.emails, where));

export const useEmailSelect = (id: number): IEmail | undefined =>
  useContextSelector(StoreContext, (state) => state.dealModule.emails[id]);

// notes
export const useAllNoteSelect = (): INote[] =>
  useContextSelector(StoreContext, (state) => Object.values(state.dealModule.notes));

export const useAllNotesObjectSelect = (): Record<number, INote> =>
  useContextSelector(StoreContext, (state) => state.dealModule.notes);

export const useNoteSelect = (id: number): INote | undefined =>
  useContextSelector(StoreContext, (state) => state.dealModule.notes[id]);

export const useNotesSelect = (where: NoteQueryBy): INote[] =>
  useContextSelector(StoreContext, (state) => querySelector(state.dealModule.notes, where));

export const useDealNoteSelect = (id: number): INote | undefined =>
  useContextSelector(StoreContext, (state) => state.dealModule.notes[id]);

export const useDealLogoSelec = (): null | IFile =>
  useContextSelector(StoreContext, (state) => state.dealModule.deal.logo);

export const useLogoToSaveSelect = (): null | IFile =>
  useContextSelector(StoreContext, (state) => state.dealModule.logoToSave);

export const useDealHierarchyOpenSelect = (): boolean =>
  useContextSelector(StoreContext, (state) => state.dealModule.hierarchyPanelOpen);

export const useCharacteristicManageData = (): IDealModule["characteristicsManage"] =>
  useContextSelector(StoreContext, (state: IStore) => state.dealModule.characteristicsManage);

export const useReachOutManage = (): IReachOutManage | null =>
  useContextSelector(StoreContext, (state: IStore) => state.dealModule.reachOutManage);

export const useMeasuresSelector = (): Measure[] =>
  useContextSelector(StoreContext, (state: IStore) => state.dealModule.measures);

export const useMeasureSelect = (id: number): Measure | undefined =>
  useContextSelector(StoreContext, (state: IStore) => state.dealModule.measures.find((measure) => measure.id === id));

export const useDealAssetClassSelect = (): IAssetClass | undefined =>
  useContextSelector(StoreContext, (state: IStore) => state.dealModule.deal.asset_class);

export const useOpportunitiesSelect = (): IOpportunities[] =>
  useContextSelector(StoreContext, (state: IStore) => state.dealModule.opportunities);

export const useDealCrunchbaseIdSelect = () =>
  useContextSelector(StoreContext, (state: IStore) => state.dealModule.deal?.crunchbase_data_id);

export const useDealCryptocurrencyIconSelect = () =>
  useContextSelector(StoreContext, (state: IStore) => state.dealModule.deal?.crypto_coin_icon);

export const useDealSharesSelect = () => useContextSelector(StoreContext, (state: IStore) => state.dealModule.shares);

export const useDealFiltersSelector = () =>
  useContextSelector(StoreContext, (state: IStore) => state.dealModule.dealFilters);

export const useDealSubAssetsSelector = (): IDeal["sub_assets"] =>
  useContextSelector(StoreContext, (state: IStore) => state.dealModule.deal.sub_assets);

export const useParentDealSelect = (): IDeal["parent"] =>
  useContextSelector(StoreContext, (state: IStore) => state.dealModule.deal.parent);

export const useDealTypeSelect = (): IDeal["type"] =>
  useContextSelector(StoreContext, (state: IStore) => state.dealModule.deal.type);

export const useIsSharedDealSelect = (): boolean => {
  return useContextSelector(
    StoreContext,
    (state: IStore) =>
      Boolean(state.dealModule.deal.id) && state.dealModule.deal.account_id !== state.userModule.account.id
  );
};

export const useDealUserRoleSelect = () => {
  return useContextSelector(StoreContext, (state: IStore) => state.dealModule.deal.deal_role);
};

export const useDealNullifyCommitmentsSelect = () => {
  return useContextSelector(StoreContext, (state: IStore) => state.dealModule.deal.nullify_unfunded_commitment);
};

export const useDealManagementFeeSettingSelect = () => {
  return useContextSelector(StoreContext, (state: IStore) => state.dealModule.deal.management_fee_setting);
};

export const useDealSuccessFeeSettingSelect = () => {
  return useContextSelector(StoreContext, (state: IStore) => state.dealModule.deal.success_fee_setting);
};

export const useDealCurrencySelect = (): DealBarCharacteristic | null => {
  return useContextSelector(StoreContext, (state: IStore) => state.dealModule.deal.currency);
};

export const useDealRatingSelect = (): string | null => {
  return useContextSelector(StoreContext, (state: IStore) => state.dealModule.deal.rating);
};

export const useDealInvestmentDetailChartSettingsSelect = (): Record<string, IInvestmentDetailChartConfig> => {
  return useContextSelector(StoreContext, (state: IStore) => state.dealModule.deal.chart_settings);
};

export const useDealRoundsWithInvestorsSelect = (): IDealRoundsWithInvestors[] => {
  return useContextSelector(StoreContext, (state: IStore) => state.dealModule.roundsWithInvestors);
};

export const useDocumentTypesSelect = (): string[] => {
  return useContextSelector(StoreContext, (state: IStore) => state.dealModule.documentTypes);
};
